import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setImageHasChanged } from "../app/userSlice";

interface Props {
	userId: number;
	initials: string;
	hasImage: boolean;
	avatarBaseUrl?: string;
	mini?: boolean;
	override?: boolean;
	className?: string;
	textClassName?: string;
	bgClassName?: string;
	borderClassName?: string;
}

export default function Avatar(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const { course } = useAppSelector((state) => state.user);

	return props.hasImage &&
		config?.enableAvatarImages &&
		(course?.showUserAvatars || props.override) ? (
		<ImageAvatar {...props} />
	) : (
		<InitialsAvatar {...props} />
	);
}

const cacheTimeout: number = 5 * 60 * 1000; // five minutes
const avatarCache: { [userId: number]: number } = {};

function hasCacheExpired(userId: number): boolean {
	const now = new Date().getTime();
	const then = avatarCache[userId];
	if (!then) return true;
	return now - then > cacheTimeout;
}

function updateCache(userId: number) {
	avatarCache[userId] = new Date().getTime();
}

function removeFromCache(userId: number) {
	delete avatarCache[userId];
}

function ImageAvatar(props: Props) {
	const { aspUrl } = useAppSelector((state) => state.site);
	const { userId, imageHasChanged } = useAppSelector((state) => state.user);

	const dispatch = useAppDispatch();

	if (imageHasChanged && userId) {
		removeFromCache(userId);
		dispatch(setImageHasChanged(false));
		return null;
	}
	if (hasCacheExpired(props.userId)) {
		updateCache(props.userId);
	}

	const avatarBaseUrl = props.avatarBaseUrl ?? `${aspUrl}/user/getuserimage`;
	const url = `${avatarBaseUrl}?userId=${props.userId}&${avatarCache[props.userId]}`;

	return (
		<div
			className={`${props.className ?? ""} relative rounded-full ${props.mini ? "h-6 w-6" : "h-10 w-10"}`}>
			<img
				className={`rounded-full ${props.borderClassName ?? "border-2 border-primary-700"} `}
				src={url}
			/>
		</div>
	);
}

function InitialsAvatar(props: Props) {
	return (
		<div
			className={`${props.className ?? ""} relative rounded-full font-button 
                ${props.textClassName ?? "text-white"}
                ${props.bgClassName ?? "bg-primary-700"}
                ${props.mini ? "h-6 w-6 text-xs" : "h-10 w-10 text-sm font-bold"}`}>
			<div
				className={`absolute h-full w-full rounded-full flex items-center justify-center
                    ${props.borderClassName ?? "border-2 border-primary-700"}`}>
				{props.initials}
			</div>
		</div>
	);
}

export function AvatarWithName(props: {
	userId: number;
	name: string;
	initials: string;
	hasImage: boolean;
}) {
	return (
		<div className="flex space-x-4 items-center">
			<Avatar
				hasImage={props.hasImage}
				initials={props.initials ?? "?"}
				userId={props.userId}
			/>
			<div>{props.name}</div>
		</div>
	);
}
